import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"

/**
 * Control-size scale (CH-22 / PP-31) — the same three names and the same
 * three heights as `buttonVariants` and `selectTriggerVariants`, so a
 * compact row of mixed controls lines up without per-call-site arithmetic:
 *
 *   default  36px  today's size — unchanged, and still the default
 *   sm       32px  the dense end of the plan's "normal" band
 *   xs       28px  the compact step, for dialogs and dense filter bars
 *
 * `default` deliberately stays the default. `Input` has hundreds of call
 * sites; moving the default would resize every form, filter bar and toolbar
 * in the app at once, including the two results grids (Wave 4) and the
 * per-surface polish clusters (Wave 5) that other tasks own. Callers opt in.
 *
 * Only height and horizontal padding change across the scale — `text-sm`
 * holds at every step. A compact control with smaller text stops matching
 * its own label, and 14px in a 28px box is comfortable.
 */
const inputVariants = cva(
  "flex w-full rounded-md border border-input bg-background text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      size: {
        default: "h-9 px-3 py-1",
        sm: "h-8 px-2.5 py-1",
        xs: "h-7 px-2 py-0.5",
      },
    },
    defaultVariants: {
      size: "default",
    },
  }
)

export interface InputProps
  // `size` is shadowed on purpose: <input> carries a native numeric `size`
  // attribute that would collide with the variant. No call site in src/ uses
  // it (checked), and `width`/`className` are the way this app sizes inputs.
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof inputVariants> {}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, size, ...props }, ref) => {
    return (
      <input
        type={type}
        data-slot="input"
        className={cn(inputVariants({ size }), className)}
        ref={ref}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

export { Input, inputVariants }
